import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Screen } from '../components/Screen'
import { EmptyState, ErrorState, SaveFailedAlert, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { MATERIALS_TRASH_KEY, notesKey, notesTrashKey, useAthlete, useGroup, useMaterialsTrash, useNotesTrash, useSingleFlight } from '../data/hooks'
import { categoryLabel, noteBody, noteKindLabel, type NoteOwner, type NoteOwnerKind } from '../data/notes'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import { SliceApiError } from '../data/types'
import { useBackTo } from './AthleteScreen'
import { LockedNote, authorLine, notesTabPath } from './NotesScreens'

/**
 * Корзина записей и материалов (решение владельца 2026-10-06). Удалённое
 * лежит 30 дней, оттуда «Вернуть», потом удаляется насовсем. Корзина - там
 * же, где удаляли: у записей - внизу вкладки «Записи» спортсмена или группы,
 * у материалов - внизу списка материалов. Общей корзины клуба нет: вернуть
 * удобнее, не теряя карточку, а записи без карточки не нужны.
 *
 * Вернуть может тот, кто мог удалить: запись - автор и владелец клуба,
 * материал - любой тренер клуба. Без сети корзина не открывается (её нет в
 * снимке).
 */

const ROOT = 'redesign'

const isKind = (e: unknown, kind: SliceApiError['kind']) => e instanceof SliceApiError && e.kind === kind

type RestoreError = 'locked' | 'forbidden' | 'gone' | 'failed'

/** «6 октября» по метке времени сервера. */
function day(ts: string): string | null {
  const d = parseIsoDate(ts?.slice(0, 10))
  return d ? formatDayShort(d) : null
}

/** «Удалено 6 октября: Мария, удалится насовсем 5 ноября». */
function deletedLine(x: { deleted_at: string; deleted_by_name: string | null; purge_at: string }): string {
  const when = day(x.deleted_at)
  const purge = day(x.purge_at)
  const who = x.deleted_by_name ? `: ${x.deleted_by_name}` : ''
  return [when ? `Удалено ${when}${who}` : null, purge ? `удалится насовсем ${purge}` : null].filter(Boolean).join(', ')
}

function RestoreAlert({ error }: { error: RestoreError | null }) {
  if (error === 'locked') return <LockedNote />
  if (error === 'forbidden')
    return (
      <div className="pc-alert" role="alert">
        Вернуть запись может её автор и владелец клуба. Ничего не изменилось.
      </div>
    )
  if (error === 'gone')
    return (
      <div className="pc-alert" role="alert">
        Этого уже нет в корзине: вернули раньше или срок 30 дней вышел. Список обновлён.
      </div>
    )
  if (error) return <SaveFailedAlert>Ничего не вернулось, попробуйте ещё раз.</SaveFailedAlert>
  return null
}

function restoreError(err: unknown): RestoreError {
  if (isKind(err, 'not_found')) return 'gone'
  if (isKind(err, 'forbidden')) return 'forbidden'
  if (isKind(err, 'limit')) return 'locked'
  return 'failed'
}

export function AthleteNotesTrashScreen() {
  const { id = '' } = useParams()
  const athlete = useAthlete(id)
  return <NotesTrashScreen kind="athlete" id={id} ownerName={athlete.data?.name} />
}

export function GroupNotesTrashScreen() {
  const { id = '' } = useParams()
  const group = useGroup(id)
  return <NotesTrashScreen kind="group" id={id} ownerName={group.data?.name} />
}

function NotesTrashScreen({ kind, id, ownerName }: { kind: NoteOwnerKind; id: string; ownerName?: string }) {
  const owner: NoteOwner = { kind, id }
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const back = useBackTo(notesTabPath(owner))
  const bin = useNotesTrash(owner)
  const flight = useSingleFlight()
  const [error, setError] = useState<RestoreError | null>(null)

  const restore = async (noteId: string) => {
    setError(null)
    try {
      const res = await flight.run(() =>
        kind === 'athlete' ? api.restoreAthleteNote(id, noteId) : api.restoreGroupNote(id, noteId),
      )
      if (!res) return
      toast('Запись возвращена')
    } catch (err) {
      setError(restoreError(err))
    } finally {
      await Promise.all([
        client.invalidateQueries({ queryKey: notesTrashKey(owner) }),
        client.invalidateQueries({ queryKey: notesKey(owner) }),
      ])
    }
  }

  let body
  if (bin.isPending) {
    body = <Skeleton rows={2} label="Загружаю удалённые записи" />
  } else if (bin.isError) {
    body = <ErrorState title="Не удалось открыть удалённые записи" onRetry={() => void bin.refetch()} />
  } else if (bin.data.length === 0) {
    body = <EmptyState title="Удалённых записей нет" text="Удалённые записи лежат здесь 30 дней, потом удаляются насовсем." />
  } else {
    body = (
      <ul className="pc-list" style={{ marginTop: 12 }} aria-label="Удалённые записи">
        {bin.data.map((n) => {
          const text = noteBody(n)
          return (
            <li key={n.id} className="pc-note-item">
              <div className="pc-note-item__main">
                {n.note_type !== 'general' && <span className="pc-badge pc-badge--neutral">{noteKindLabel(n.note_type)}</span>}
                <div className="pc-kv__label">{n.title}</div>
                {text && (
                  <div className="pc-kv__value" style={{ whiteSpace: 'pre-wrap' }}>
                    {text}
                  </div>
                )}
                <div className="pc-field__hint">{authorLine(n)}</div>
                <div className="pc-field__hint">{deletedLine(n)}</div>
                {!n.can_restore && <div className="pc-field__hint">Вернуть может автор записи и владелец клуба.</div>}
              </div>
              {n.can_restore && (
                <button
                  type="button"
                  className="pc-btn"
                  aria-label={`Вернуть запись «${n.title}»`}
                  disabled={flight.busy}
                  onClick={() => void restore(n.id)}
                >
                  Вернуть
                </button>
              )}
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <Screen title="Удалённые записи" subtitle={ownerName} back={back} hideNav>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        Удалённые записи лежат здесь 30 дней, потом удаляются насовсем.
      </p>
      <RestoreAlert error={error} />
      {body}
    </Screen>
  )
}

/** Корзина материалов направления: вернуть может любой тренер клуба. */
export function MaterialsTrashScreen() {
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const back = useBackTo('/club/materials')
  const bin = useMaterialsTrash()
  const flight = useSingleFlight()
  const [error, setError] = useState<RestoreError | null>(null)

  const restore = async (materialId: string) => {
    setError(null)
    try {
      const res = await flight.run(() => api.restoreMaterial(materialId))
      if (!res) return
      toast(`Материал возвращён: ${res.title}`)
    } catch (err) {
      setError(restoreError(err))
    } finally {
      await Promise.all([
        client.invalidateQueries({ queryKey: MATERIALS_TRASH_KEY }),
        client.invalidateQueries({ queryKey: [ROOT, 'materials'] }),
        client.invalidateQueries({ queryKey: [ROOT, 'material'] }),
      ])
    }
  }

  let body
  if (bin.isPending) {
    body = <Skeleton rows={2} label="Загружаю удалённые материалы" />
  } else if (bin.isError) {
    body = <ErrorState title="Не удалось открыть удалённые материалы" onRetry={() => void bin.refetch()} />
  } else if (bin.data.length === 0) {
    body = <EmptyState title="Удалённых материалов нет" text="Удалённые материалы лежат здесь 30 дней, потом удаляются насовсем." />
  } else {
    body = (
      <ul className="pc-list" style={{ marginTop: 12 }} aria-label="Удалённые материалы">
        {bin.data.map((m) => (
          <li key={m.id} className="pc-note-item">
            <div className="pc-note-item__main">
              <span className="pc-badge pc-badge--neutral">{categoryLabel(m.category)}</span>
              <div className="pc-kv__label">{m.title}</div>
              <div className="pc-field__hint">{deletedLine(m)}</div>
            </div>
            <button
              type="button"
              className="pc-btn"
              aria-label={`Вернуть материал «${m.title}»`}
              disabled={flight.busy}
              onClick={() => void restore(m.id)}
            >
              Вернуть
            </button>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Screen title="Удалённые материалы" back={back} hideNav>
      <p className="pc-muted" style={{ margin: '8px 0 0', fontSize: 14 }}>
        Удалённые материалы лежат здесь 30 дней, потом удаляются насовсем. Штаб на них не опирается, пока их не вернут.
      </p>
      <RestoreAlert error={error} />
      {body}
    </Screen>
  )
}
